import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import flash from "../lib/flashes.js";
import { jsonRequest, withCsrf } from "../lib/hudini/csrf.js"
import { initTooltips } from "../lib/hudini/tooltips.js"

// How far from the bottom of the thread still counts as "following along", and
// so keeps auto-scrolling as tokens arrive. A little slack absorbs sub-pixel
// scroll heights and the last line's margin.
const SCROLL_ANCHOR_SLACK = 80

// The turn endpoints reject with `{ error: "..." }` (prompt too long, a turn
// already running), which is worth showing verbatim instead of a generic
// failure. Falls back for responses with no JSON body.
async function errorMessage(response) {
  try {
    const body = await response.json()
    return body.error || "Request failed"
  } catch (_) {
    return "Request failed"
  }
}

// Owns the composer. The reply is generated in the background and streamed to
// the browser over Turbo Streams (`turbo_stream_from @chat`): the server appends
// the turns, pours batched token deltas into the assistant placeholder, and
// swaps in the authoritative HTML when done. This controller only sends the
// prompt and reflects scroll/tooltips as streamed updates land. Skills and file
// context live in sibling controllers reached via outlets.
export default class extends Controller {
  static targets = ["prompt", "messages", "sendButton", "stopButton", "welcome", "promptDock", "composer"]
  static values = {
    messagesUrl: String,
    // POSTed when the user presses Stop; the background run checks the signal
    // and bails out, keeping whatever streamed so far.
    cancelUrl: String,
  }

  // The skills and context controllers share this root element, so we reach them
  // by identifier rather than an outlet (Stimulus can't point an outlet at its
  // own host element).
  get skillsController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "hudini-skills")
  }

  get contextController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "hudini-context")
  }

  get modelController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "hudini-model")
  }

  connect() {
    this.scrollToBottom()
    this.initTippy()
    if (this.hasPromptTarget) {
      this.updateEmptyState()
      // contenteditable ignores the `autofocus` attribute, so focus it manually.
      if (this.promptTarget.hasAttribute("autofocus")) this.focusEditorEnd()
    }
    // Streamed updates (appended turns, deltas, final message) arrive as Turbo
    // Stream broadcasts; keep the thread scrolled and tooltips initialized as
    // they render. The listener is on `document` because that is where Turbo
    // dispatches, so each render is filtered down to this panel's own nodes.
    this.onStreamRender = (event) => {
      const target = this.streamTarget(event)
      if (!target) return

      // Measured before the DOM changes: a user who scrolled up to read is left
      // alone, one sitting at the bottom stays pinned there.
      const pinned = this.nearBottom()
      const preview = this.isPreviewTarget(target)
      requestAnimationFrame(() => {
        if (pinned) this.scrollToBottom()
        // Token deltas carry no tooltips, and re-initializing tippy across the
        // panel every 75ms is the expensive half of this handler.
        if (!preview) this.initTippy()
      })
    }
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    if (this.onStreamRender) document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  // The Stop button only exists while a turn is running (rendered by the
  // broadcast composer state). Its (dis)connection is the authoritative
  // busy/idle signal.
  stopButtonTargetConnected() {
    this.sending = false
  }

  sendButtonTargetConnected() {
    this.sending = false
  }

  // True while a turn is in flight: either the brief window between POST and the
  // server's "busy" broadcast, or once the Stop button is on screen.
  get streaming() {
    return this.hasStopButtonTarget || this.sending === true
  }

  // --- Sending ---------------------------------------------------------------

  async submit(event) {
    if (event) event.preventDefault()
    const prompt = this.promptText()
    if (!prompt) return

    // Capture the selected skills and mentioned records before clearing the
    // editor, since the tokens (the source of truth) live in the editor we're
    // about to reset.
    const skillIds = this.selectedSkillIds
    const contextRecords = this.contextRecords
    this.resetTextarea()
    await this.sendPrompt(prompt, skillIds, contextRecords)
  }

  // Fires the turn and returns. The server persists the turns and enqueues the
  // background run. The create response carries Turbo Streams for the user
  // bubble + thinking placeholder (so this tab never depends on ActionCable);
  // token deltas and the finished reply still arrive over the chat subscription.
  async sendPrompt(prompt, skillIds = this.selectedSkillIds, contextRecords = []) {
    if (!prompt || this.streaming) return

    this.sending = true
    // Both blank-state nodes: the greeting (in the thread) and the starter
    // chips (above the composer).
    this.welcomeTargets.forEach((el) => el.remove())
    if (this.contextController) this.contextController.clearPending()

    // Providers without a model catalog render no picker, so the tier is left
    // off the payload entirely rather than sent as a meaningless default.
    const body = { prompt, ai_prompt_ids: skillIds }
    if (contextRecords.length > 0) body.context_records = contextRecords
    const tier = this.selectedModelTier
    if (tier) body.model_tier = tier

    try {
      const request = jsonRequest("POST", body)
      const response = await fetch(this.messagesUrlValue, {
        ...request,
        headers: { ...request.headers, Accept: "text/vnd.turbo-stream.html" },
      })
      if (!response.ok) throw new Error(await errorMessage(response))

      const html = await response.text()
      if (html) {
        const pinned = this.nearBottom()
        Turbo.renderStreamMessage(html)
        if (pinned) requestAnimationFrame(() => this.scrollToBottom())
        this.initTippy()
      }
    } catch (error) {
      this.sending = false
      flash(error.message || "Request failed", "error")
    }
  }

  // Asks the server to cancel the in-flight turn. The composer is restored when
  // the run's terminal "idle" state broadcasts back.
  stop() {
    if (!this.hasCancelUrlValue) return
    fetch(this.cancelUrlValue, withCsrf({ method: "POST" })).catch(() => {})
  }

  // Receives composed answers from the `hudini-questions` card and sends them as
  // the next message so the assistant can complete the action.
  submitAnswer(event) {
    const text = event.detail && event.detail.text
    if (text) this.sendPrompt(text)
  }

  // Suggestion chips: drop a prompt template into the composer and focus it so
  // the user can tweak it before sending, rather than firing immediately.
  prefillPrompt(event) {
    event.preventDefault()
    if (this.streaming) return
    this.setEditorText(event.currentTarget.dataset.prompt || "")
    this.focusEditorEnd()
  }

  // --- Edit past user message (Claude/ChatGPT style) -------------------------

  // Opens the inline editor on a user bubble. Only one edit at a time; starting
  // another cancels the first. Disabled while a turn is streaming.
  startEdit(event) {
    event.preventDefault()
    if (this.streaming) return

    const message = event.currentTarget.closest(".openai-message--user")
    if (!message) return

    this.cancelEdit()
    message.classList.add("openai-message--editing")
    this.editingMessage = message
    this.editingUrl = event.currentTarget.dataset.updateUrl

    const input = message.querySelector(".openai-message__edit-input")
    if (input) {
      this.editingOriginal = input.value
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }

  cancelEdit(event) {
    if (event) event.preventDefault()
    if (!this.editingMessage) return

    // Put back whatever was in the bubble when edit opened so a cancelled
    // draft doesn't linger if the user opens it again.
    const input = this.editingMessage.querySelector(".openai-message__edit-input")
    if (input && this.editingOriginal != null) input.value = this.editingOriginal

    this.editingMessage.classList.remove("openai-message--editing")
    this.editingMessage = null
    this.editingUrl = null
    this.editingOriginal = null
  }

  async submitEdit(event) {
    if (event) event.preventDefault()
    if (!this.editingMessage || !this.editingUrl || this.streaming) return

    const input = this.editingMessage.querySelector(".openai-message__edit-input")
    const prompt = (input?.value || "").trim()
    if (!prompt) return

    const url = this.editingUrl
    this.editingMessage.classList.remove("openai-message--editing")
    this.editingMessage = null
    this.editingUrl = null
    this.editingOriginal = null

    this.sending = true
    try {
      const response = await fetch(url, jsonRequest("PATCH", { prompt }))
      if (!response.ok) throw new Error(await errorMessage(response))
    } catch (error) {
      this.sending = false
      flash(error.message || "Request failed", "error")
    }
  }

  // Cmd/Ctrl+Enter saves; Escape cancels. Plain Enter inserts a newline.
  handleEditKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.cancelEdit()
    } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.submitEdit(event)
    }
  }

  // Enter sends; Escape stops an in-flight turn. Skill-menu navigation is
  // handled by the skills controller, which stops propagation before this runs.
  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.submit(event)
    } else if (event.key === "Escape" && this.streaming) {
      // Own the key so the surrounding sidepanel doesn't also close on Escape.
      event.preventDefault()
      event.stopPropagation()
      this.stop()
    }
  }

  handleInput() {
    this.updateEmptyState()
  }

  // --- Composer text (contenteditable) ---------------------------------------

  // The message text the user typed, excluding inline skill tokens (those are
  // sent separately as ids). Block elements and <br>s become newlines.
  promptText() {
    if (!this.hasPromptTarget) return ""
    return this.serializeEditor(this.promptTarget)
      .replace(/\u00A0/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .trim()
  }

  serializeEditor(node) {
    let text = ""
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.dataset && child.dataset.skillId) return
        if (child.tagName === "BR") {
          text += "\n"
        } else if (child.tagName === "DIV" || child.tagName === "P") {
          if (text && !text.endsWith("\n")) text += "\n"
          text += this.serializeEditor(child)
        } else {
          text += this.serializeEditor(child)
        }
      }
    })
    return text
  }

  setEditorText(value) {
    if (!this.hasPromptTarget) return
    this.promptTarget.textContent = value || ""
    this.updateEmptyState()
    this.focusEditorEnd()
  }

  focusEditorEnd() {
    const editor = this.promptTarget
    editor.focus()
    const range = document.createRange()
    range.selectNodeContents(editor)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  }

  // Toggles the empty class that drives the CSS placeholder, since a
  // contenteditable is never truly `:empty` once focused.
  updateEmptyState() {
    if (!this.hasPromptTarget) return
    const hasTokens = this.promptTarget.querySelectorAll("[data-skill-id], [data-record-id]").length > 0
    const empty = !hasTokens && this.promptText() === ""
    this.promptTarget.classList.toggle("hudini__editor--empty", empty)
  }

  // Paste as plain text so external rich HTML never lands in the editor. Files
  // are handled by the context controller's paste handler.
  handlePaste(event) {
    const data = event.clipboardData
    if (!data || (data.files && data.files.length > 0)) return

    const text = data.getData("text/plain")
    if (!text) return

    event.preventDefault()
    const sel = window.getSelection()
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      const node = document.createTextNode(text)
      range.insertNode(node)
      range.setStartAfter(node)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
    } else {
      this.promptTarget.appendChild(document.createTextNode(text))
    }
    this.updateEmptyState()
  }

  resetTextarea() {
    if (!this.hasPromptTarget) return
    this.promptTarget.innerHTML = ""
    this.updateEmptyState()
  }

  get selectedSkillIds() {
    return this.skillsController ? this.skillsController.selectedIdsArray : []
  }

  // Records @-mentioned in the composer (deduped, in-document order). The
  // server re-authorizes each one before attaching it as context.
  get contextRecords() {
    if (!this.hasPromptTarget) return []

    const seen = new Set()
    const records = []
    this.promptTarget.querySelectorAll("[data-record-type][data-record-id]").forEach((mention) => {
      const key = `${mention.dataset.recordType}:${mention.dataset.recordId}`
      if (seen.has(key)) return
      seen.add(key)
      records.push({ record_type: mention.dataset.recordType, record_id: mention.dataset.recordId })
    })
    return records
  }

  get selectedModelTier() {
    return this.modelController ? this.modelController.selectedTier : null
  }

  // --- Questions dock (coordinated via the hudini-questions outlet) ----------

  get dockElement() {
    return this.hasPromptDockTarget ? this.promptDockTarget : null
  }

  // Reveals the footer dock and hides the composer while an `ask_user` prompt is
  // awaiting an answer.
  dockQuestions() {
    if (this.hasPromptDockTarget) this.promptDockTarget.classList.add("hudini__prompt-dock--active")
    if (this.hasComposerTarget) this.composerTarget.classList.add("hidden")
  }

  // Clears the dock and restores the composer. Safe to call repeatedly.
  releaseDock() {
    if (this.hasPromptDockTarget) {
      this.promptDockTarget.innerHTML = ""
      this.promptDockTarget.classList.remove("hudini__prompt-dock--active")
    }
    if (this.hasComposerTarget) this.composerTarget.classList.remove("hidden")
  }

  // Re-runs the latest Hudini reply from the preceding user turn.
  async redo(event) {
    event.preventDefault()
    if (this.streaming) return

    const url = event.currentTarget.dataset.redoUrl
    if (!url) return

    this.sending = true
    try {
      const response = await fetch(url, withCsrf({ method: "POST" }))
      if (!response.ok) throw new Error("Request failed")
    } catch (error) {
      this.sending = false
      flash(error.message || "Request failed", "error")
    }
  }

  // --- Links -----------------------------------------------------------------

  // The panel is a turbo-frame: a same-tab navigation replaces the chat with
  // the linked page. Markdown links already carry target="_blank", but Turbo
  // Frame interception is inconsistent — force a real new tab, matching how
  // mention pills open records.
  openContentLink(event) {
    const link = event.target.closest?.("a[href]")
    if (!link || !this.hasMessagesTarget || !this.messagesTarget.contains(link)) return
    if (!link.closest(".openai-message__content, .openai-message__context")) return

    const href = link.getAttribute("href") || ""
    if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("javascript:")) return

    event.preventDefault()
    event.stopPropagation()
    window.open(link.href, "_blank", "noopener,noreferrer")
  }

  // --- Copy ------------------------------------------------------------------

  async copyText(event) {
    const content = event.target.closest(".openai-message").querySelector(".openai-message__content")
    await this.copy(content.textContent.trim(), event.target)
  }

  async copyHtml(event) {
    const content = event.target.closest(".openai-message").querySelector(".openai-message__content")
    await this.copy(content.innerHTML, event.target)
  }

  async copy(value, target) {
    try {
      await navigator.clipboard.writeText(value)
      const button = target.closest("button")
      const original = button.innerHTML
      button.innerHTML = '<i class="far fa-check"></i>'
      setTimeout(() => { button.innerHTML = original }, 2000)
    } catch (_) {}
  }

  // --- Helpers ---------------------------------------------------------------

  scrollToBottom() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  // The element a `<turbo-stream>` is about to change, but only when it belongs
  // to this panel — Turbo streams from elsewhere in the app (imports, exports,
  // notifications) must not scroll the chat or re-run tooltips.
  streamTarget(event) {
    const id = event.target.getAttribute("target")
    if (!id) return null

    const target = document.getElementById(id)
    return target && this.element.contains(target) ? target : null
  }

  isPreviewTarget(target) {
    return target.classList.contains("hudini-stream")
  }

  // Whether the thread is scrolled to (or within a line of) the bottom.
  nearBottom() {
    if (!this.hasMessagesTarget) return false

    const el = this.messagesTarget
    return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_ANCHOR_SLACK
  }

  initTippy() {
    initTooltips(this.element)
  }
}
